// Domain error types for the HARNESS-03 thin-slice commands. Each carries
// the HTTP status the api/ layer maps it to, so the Fastify route handlers
// stay thin translators rather than re-deriving status codes from message
// text.

export class DomainError extends Error {
  readonly statusCode: number;
  readonly code: string;

  constructor(message: string, statusCode: number, code: string) {
    super(message);
    this.name = "DomainError";
    this.statusCode = statusCode;
    this.code = code;
  }
}

export class BillingPausedError extends DomainError {
  /** NETBILLPAUSE1: `notice` is the one plain sentence that names why (and whose bill), built from data by billingPauseNotice.ts. */
  constructor(notice?: string) {
    super(
      notice ??
        "Subscription billing needs attention. Normal location work is paused; refunds, reconciliation, closeouts and exports remain available.",
      403,
      "billing_paused",
    );
    this.name = "BillingPausedError";
  }
}

/** OWNSUSP1 (PRD P0.32): the refusal a suspended or cancelled customer or location gets on sign-in and on every route that
 * is not on the suspended allow list. The message names the state and tells the user who to contact. */
export class AccountStandingError extends DomainError {
  readonly state: "suspended" | "cancelled";
  constructor(state: "suspended" | "cancelled") {
    super(
      state === "suspended"
        ? "This location is suspended. Contact Snip & Sip to restore access."
        : "This location's account is cancelled. Contact Snip & Sip.",
      403,
      "account_standing",
    );
    this.name = "AccountStandingError";
    this.state = state;
  }
}

export class NotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "not_found");
    this.name = "NotFoundError";
  }
}

// TEN21 (docs/build-briefs/TEN21-franchisor-no-location.md). A franchisor may have no location at all (owner,
// 2026-10-09). A route that needs a location answers this plainly instead of creating a "Main Location" for it.
export class TenantHasNoLocationError extends DomainError {
  constructor() {
    super(
      "This customer has no location. A franchisor does not need its own location.",
      409,
      "tenant_has_no_location",
    );
    this.name = "TenantHasNoLocationError";
  }
}

// LOC1-S2 (docs/build-briefs/LOC1-S2-ticket-location-id.md section 1.3; DEC-319 (2)). A ticket's location is
// the location of the tablet it is rung on. A session with no bound device has none, so it cannot open a
// ticket, and nothing is written. There is deliberately no fallback to "the tenant's only location".
export class DeviceNotBoundError extends DomainError {
  constructor() {
    super(
      "This device is not set up for a location. Ask a manager for a setup code.",
      409,
      "device_not_bound",
    );
    this.name = "DeviceNotBoundError";
  }
}

// TERM4 (docs/build-briefs/TERM4-card-readers-per-bar.md). A card reader belongs to one location, and a tablet may
// charge only through a reader at its own location. The message names no other location and no provider reference.
export class TerminalOtherLocationError extends DomainError {
  constructor() {
    super(
      "That card reader belongs to a different location. Choose a reader at this location from the Card reader item in the account menu.",
      409,
      "terminal_other_location",
    );
    this.name = "TerminalOtherLocationError";
  }
}

// LOC1-S2 (brief section 1.3, "Idempotent replay"). The idempotency key of a ticket that was opened at one
// location was presented by a tablet at another. Returning that ticket would hand one location another location's ticket,
// so the call is refused and nothing about the original changes.
export class IdempotencyKeyOtherLocationError extends DomainError {
  constructor(
    message = "This request was already used for a ticket at a different location. Start the ticket again.",
  ) {
    super(message, 409, "idempotency_key_other_location");
    this.name = "IdempotencyKeyOtherLocationError";
  }
}

// INVLOC1-S1 (docs/build-briefs/INVLOC1-S1-stock-ledger-and-counts-per-location.md section 3.3). The same refusal for
// a stock or count write: the key of a receipt, a movement, a count session or a stock flag that was recorded at one
// location was presented by a tablet at another. Returning that row would hand one location another location's record, so the call
// is refused and nothing about the original changes. The code is the one LOC1-S2 defined for tickets.
export function stockIdempotencyKeyOtherLocationError(): IdempotencyKeyOtherLocationError {
  return new IdempotencyKeyOtherLocationError(
    "This request was already used at a different location. Start it again from this device.",
  );
}

// INVLOC1-S1 (umbrella brief section 3.1; DEC-322 (1)). A record-bound act (close or reassign a count, correct a
// receipt, reverse a movement, read a count's walk) from a tablet set up for a different location than the record's. The
// message names no other location.
export class RecordLocationMismatchError extends DomainError {
  constructor() {
    super(
      "That record belongs to a different location. Use a device set up for that location.",
      409,
      "record_location_mismatch",
    );
    this.name = "RecordLocationMismatchError";
  }
}

// INVLOC1-S1 (brief section 3.3). Marking a drink Prepared (or undoing it) from a tablet at a different location than the
// ticket's. The posting would take the ticket's location, so the stock leaving is the right location's; but a tablet at B has
// no business marking location A's ticket made.
export class TicketLocationMismatchError extends DomainError {
  constructor() {
    super(
      "This ticket belongs to a different location. Use a device set up for that location.",
      409,
      "ticket_location_mismatch",
    );
    this.name = "TicketLocationMismatchError";
  }
}

// INVLOC1-S1 (umbrella brief section 3.2). A planning request (a read, or a write about what a location stocks) from a
// session with no bound device that named no location either. Nothing guesses one.
export class LocationRequiredError extends DomainError {
  constructor() {
    super(
      "Choose which location this is for, or use a device set up for a location.",
      409,
      "location_required",
    );
    this.name = "LocationRequiredError";
  }
}

// INVLOC1-S1 (umbrella brief section 3.2). A planning request named a location of the same franchisee that this person
// may not act at. Until user_locations exists (ADR-002 Slice 4) nobody may name any location but their tablet's own.
export class LocationNotPermittedError extends DomainError {
  constructor() {
    super("You cannot work with that location from here.", 403, "location_not_permitted");
    this.name = "LocationNotPermittedError";
  }
}

// LOC2 (docs/build-briefs/LOC2-second-bar-user-locations.md). A person signed in at a tablet whose location they are not
// assigned to. The message names no location and no other person.
export class NotAssignedToLocationError extends DomainError {
  constructor() {
    super(
      "You are not set up to work at this location. Ask a manager.",
      403,
      "not_assigned_to_location",
    );
    this.name = "NotAssignedToLocationError";
  }
}

// LOC2. The customer already has a location and the deployment has not turned on adding another (SECOND_LOCATION_ENABLED,
// src/config/secondLocation.ts) because closeout and Sales by day are not yet per location (LOC2-S2).
export class SecondLocationNotEnabledError extends DomainError {
  constructor() {
    super(
      "Adding another location is not available yet. Ask SnipSip to turn it on for your account.",
      409,
      "second_location_not_enabled",
    );
    this.name = "SecondLocationNotEnabledError";
  }
}

// LOC2. The software owner tried to add a location to a customer that is not provisioned or active.
export class CustomerNotOpenForLocationError extends DomainError {
  constructor() {
    super("This customer is not active, so a location cannot be added to it.", 409, "tenant_not_open");
    this.name = "CustomerNotOpenForLocationError";
  }
}

// LOC2. Two locations of one customer cannot share a name (ignoring case and surrounding spaces).
export class LocationNameTakenError extends DomainError {
  constructor() {
    super(
      "Another location already has that name. Choose a different name.",
      409,
      "location_name_taken",
    );
    this.name = "LocationNameTakenError";
  }
}

// Pay and Close are rejected with a clear error while any included line is
// Not Prepared, per solution-architecture.md Section 7.3.
export class LinesNotPreparedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "lines_not_prepared");
    this.name = "LinesNotPreparedError";
  }
}

// ID1 (docs/build-briefs/ID1-id-verified-ticket-action.md). The ID Verified action refused on a state the floor
// would not have offered: the location's switch is off, the ticket has no alcohol, the ticket already carries a check (use
// Change), or Change was asked on a ticket with none. All 409, plain wording, no ID data in any message.
export class IdVerificationRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "IdVerificationRefusedError";
  }
}

// ID1-F2 (docs/build-briefs/ID1-F2-id-check-required-mode.md; DEC-398). Pay refused at a location in Required mode: the
// ticket has alcohol and neither an ID check nor a recorded skip. 409, plain wording, no ID data.
export class IdCheckRequiredError extends DomainError {
  constructor() {
    super("ID not checked. Verify the ID or skip the check first.", 409, "id_check_required");
    this.name = "IdCheckRequiredError";
  }
}

// CASH1-S1 (docs/build-briefs/CASH1-S1-cash-drawers.md). A drawer command refused on a state the caller could not have
// known to avoid: the device already has an open drawer, the drawer is already closed, or it belongs to another tablet.
// All 409, plain wording, no figures.
export class CashDrawerRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "CashDrawerRefusedError";
  }
}

// CASH1-S2 (docs/build-briefs/CASH1-S2-cash-tender.md). Cash refused because this location has cash switched off in Settings.
export class CashNotEnabledError extends DomainError {
  constructor() {
    super("This location is not taking cash.", 409, "cash_not_enabled");
    this.name = "CashNotEnabledError";
  }
}

// CASH1-S2. Cash taken or refunded on a device with no open drawer, or when the open drawer was closed meanwhile.
export class CashDrawerNotOpenError extends DomainError {
  constructor() {
    super("Open a cash drawer on this device first.", 409, "cash_drawer_not_open");
    this.name = "CashDrawerNotOpenError";
  }
}

// CASH1-S2. The cash handed over is less than the amount due. The ticket is untouched.
export class CashTenderedBelowDueError extends DomainError {
  constructor() {
    super("The cash handed over is less than the amount due.", 409, "cash_tendered_below_due");
    this.name = "CashTenderedBelowDueError";
  }
}

// RCPT2 (docs/build-briefs/RCPT2-emailed-receipts-and-paperless.md; DEC-403). A receipt choice or send refused on a state the
// caller could not have known to avoid: a Printed choice at a location that is Email only, or a resend while a send is in
// progress. 409, plain wording, no address in any message.
export class ReceiptEmailRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "ReceiptEmailRefusedError";
  }
}

// RCPT1-S1 (docs/build-briefs/RCPT1-S1-tip-mode-and-overcapture.md; DEC-401). A tip written on a receipt is over the
// limit for its authorization (the greater of 50% and $50, src/config/receiptTips.ts). 409, refused before any provider
// call, plain wording. The figures are for the screen; they are the caller's own ticket's, not another tenant's.
export class ReceiptTipOverLimitError extends DomainError {
  readonly capCents: number;
  constructor(capCents: number) {
    super(
      "That tip is over the limit for this sale. Enter a smaller tip.",
      409,
      "receipt_tip_over_cap",
    );
    this.name = "ReceiptTipOverLimitError";
    this.capCents = capCents;
  }
}

// RCPT1-S1. The card does not allow adding a tip after the authorization. Nothing was captured.
export class ReceiptTipNotSupportedError extends DomainError {
  constructor() {
    super(
      "This card cannot take a tip after the sale. Charge it with no tip.",
      409,
      "receipt_tip_not_supported",
    );
    this.name = "ReceiptTipNotSupportedError";
  }
}

// RCPT1-S1. Tip mode cannot be switched while a ticket at the location is awaiting a tip. 409.
export class TipModeSwitchBlockedError extends DomainError {
  constructor(awaitingCount: number) {
    super(
      awaitingCount === 1
        ? "One ticket is waiting for a tip. Finish it first."
        : `${awaitingCount} tickets are waiting for a tip. Finish them first.`,
      409,
      "tip_mode_awaiting_tips",
    );
    this.name = "TipModeSwitchBlockedError";
  }
}

// RCPT1-S4. Carrying an awaiting-tip ticket to the next business day refused: the capture deadline is unknown or too near,
// the authorization is not an active receipt sale, or the ticket already left its day. 409.
export class ReceiptCarryOverRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "ReceiptCarryOverRefusedError";
  }
}

// INVXFER1 (docs/build-briefs/INVXFER1-stock-transfers-between-bars.md). A stock transfer refused on a state the
// caller could not have known to avoid: the location does not hold that much, the transfer was already received or
// cancelled, or the receiving location is switched off. All 409.
export class InventoryTransferRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "InventoryTransferRefusedError";
  }
}

// Request-shape problems caught before any domain write is attempted.
export class ValidationError extends DomainError {
  constructor(message: string) {
    super(message, 400, "invalid_request");
    this.name = "ValidationError";
  }
}

// MODRM1-F1: missing ingredient configuration refuses adding a drink, while
// batch previews can mark only this line unavailable without hiding other prices.
export class IngredientConfigurationUnavailableError extends ValidationError {
  constructor(message: string) {
    super(message);
    this.name = "IngredientConfigurationUnavailableError";
  }
}

// INV19 Slice 2 (docs/build-briefs/INV19-pour-size-price-override.md,
// "Slice 2 design"). Extends ValidationError rather than sitting beside it,
// deliberately: every existing catch site and status mapping keeps its
// exact current behavior unedited, since this is still a plain 400
// invalid_request to any caller that does not know this subclass exists.
// The subclass exists only so the batched price-quote route's per-line
// loop can ask `instanceof LinePricingConflictError` and tell "this one
// line's configured price is in conflict" (a pour-size override resolving
// negative against its tier, per DEC-139) apart from "this request is
// malformed", without changing what any other route or catch site does.
export class LinePricingConflictError extends ValidationError {
  constructor(message: string) {
    super(message);
    this.name = "LinePricingConflictError";
  }
}

// PQ1 (docs/build-briefs/PQ1-price-quote-per-line-catch.md). Extends
// ValidationError, the identical shape LinePricingConflictError already
// establishes: every existing catch site and status mapping keeps its
// exact current behavior (still a plain 400 invalid_request to add-line,
// change-liquor, or any caller that does not know this subclass exists).
// The subclass exists only so the batched price-quote route's cocktail
// loop can ask `instanceof LineSelectionConflictError` and degrade
// exactly the two conditions PQ1 names -- an 86 flag set since the
// client's menu was last loaded, and a category mismatch -- to an
// unpriceable line rather than failing every other line in the same
// batch. Every OTHER ValidationError resolveAndPriceLine can throw
// (a missing required liquor selection, an unresolvable id, an inactive
// product, a pour-size or modifier mismatch) stays a bare ValidationError
// on purpose and keeps failing the whole batch; see that brief's Section 1
// for why each is excluded.
export class LineSelectionConflictError extends ValidationError {
  constructor(message: string) {
    super(message);
    this.name = "LineSelectionConflictError";
  }
}

// A refusal the CLIENT must recognize reliably (VOC1-F4-F3): a modifier a
// caller selected has since been retired from the catalog. Extends
// DomainError DIRECTLY, not ValidationError, precisely because
// LineSelectionConflictError/LinePricingConflictError's own precedent
// immediately above is the wrong shape for this: those two exist so a
// SERVER-side `instanceof` check can degrade one line without changing
// what the client observes (both still send the generic "invalid_request"
// code, inherited unchanged from ValidationError's own constructor, since
// neither passes its own code). This error needs the opposite: a
// client-visible, stable code the floor sheet can switch on instead of
// matching the message's prose, the same shape ApprovalRequiredError
// (src/domain/permissions.ts) already established and
// ui/src/payCloseErrorMessages.ts's translatePayCloseError already
// consumes for Pay/Close. `retiredValue` mirrors
// ApprovalRequiredError.permissionId: a field the caller is already
// entitled to, since it is the exact value they submitted in
// modifierSelections.
export class ModifierRetiredError extends DomainError {
  readonly retiredValue: string;

  constructor(message: string, retiredValue: string) {
    super(message, 400, "modifier_retired");
    this.name = "ModifierRetiredError";
    this.retiredValue = retiredValue;
  }
}

// A catalog create/reactivate would collide with another currently-active
// row's per-tenant unique name (liquor_products or menu_drinks). Added by
// ALPHA-01; see db/schema.ts's "*_tenant_active_name_key" partial unique
// indexes. U6 (2026-09-02, DEC-041 item 4) reuses this same error for
// reactivateUser's own collision, translated from users_tenant_active_
// display_name_key (migration 0019): reactivating a person whose display
// name has since been taken by an active new hire is blocked with a clear
// error naming the collision, never an automatic suffix. The owner's
// ruling: a manager renames one of the two people first.
export class DuplicateActiveNameError extends DomainError {
  constructor(message: string) {
    super(message, 409, "duplicate_active_name");
    this.name = "DuplicateActiveNameError";
  }
}

// ALPHA-05: rename is rejected once a tab has closed. src/domain/tabs.ts's
// renameOrder pre-checks this before writing anything, the same shape
// LinesNotPreparedError's pre-check has in src/domain/close.ts. Also
// raised by src/domain/close.ts's own closeOrder when its state-gated
// UPDATE matches zero rows (a Close that lost a race against another
// command that closed the order first) and by renameOrder's own
// state-gated UPDATE for the identical race against a concurrent Close.
export class OrderClosedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "order_closed");
    this.name = "OrderClosedError";
  }
}

// ALPHA-05: reopen only ever moves an order out of the closed state, per
// solution-architecture.md Section 7.1's "Reopened" alternate outcome.
// src/domain/tabs.ts's reopenOrder pre-checks this before writing anything,
// and also raises it from its state-gated UPDATE when that UPDATE matches
// zero rows (a reopen that lost a race against another concurrent reopen
// of the same closed order, using a different idempotency key).
export class OrderNotClosedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "order_not_closed");
    this.name = "OrderNotClosedError";
  }
}

// ALPHA-08, redefined 2026-08-21 per the owner's delta-payment ruling
// (docs/superpowers/plans/2026-08-19-alpha-work-breakdown.md's Amendment
// 2026-08-21, resolving architect gate findings 4 and 5): remove-line and
// change-liquor are rejected once the SPECIFIC line they target is covered
// by a captured payment (src/domain/payments.ts's payment_line_coverage),
// never by whether the order has ANY captured payment. An order with one
// covered line and one uncovered line still allows editing the uncovered
// line; only the covered one is blocked. src/domain/lineEditing.ts's
// assertLineNotCovered throws this before writing anything, and again from
// its own state-gated UPDATEs' rowCount checks when the precondition
// passed against a stale read (the identical race-defense shape as
// OrderClosedError/OrderNotClosedError in src/domain/tabs.ts). The class
// and error code keep their original pre-2026-08-21 names to avoid
// churning every existing call site and test assertion that already
// checks `instanceof OrderAlreadyPaidError`; only the scope of what
// triggers it changed, not the shape callers see.
export class OrderAlreadyPaidError extends DomainError {
  constructor(message: string) {
    super(message, 409, "order_already_paid");
    this.name = "OrderAlreadyPaidError";
  }
}

// ALPHA-08, added 2026-08-21 per the owner's delta-payment ruling: Pay is
// rejected when every active line on the order is already covered by a
// prior captured payment (or the order has no active lines at all), since
// there is nothing left to charge. src/domain/payments.ts's payOrder
// throws this before writing anything (before any payment_attempts row is
// inserted), inside the same transaction that reads the order's uncovered
// lines. The UI is expected to disable Pay proactively once it can see
// there is nothing unpaid (harness/ui/src/main.ts), so reaching this error
// in practice means the UI's own state went stale, not the ordinary path.
export class NothingUnpaidError extends DomainError {
  constructor(message: string) {
    super(message, 409, "nothing_unpaid");
    this.name = "NothingUnpaidError";
  }
}

// CL1, owner ruling 2026-08-28, verbatim: "you should not be able to
// close a ticket until the entire amount is paid in full and then the
// ticket should close itself.
//
// Raised by src/domain/close.ts's closeOrder when the ticket still owes
// something, computed by the one shared definition in
// src/domain/orderBalance.ts (the discounted subtotal of the lines Pay
// itself would charge for). Distinct from OrderClosedError, which is
// about the order's STATE, and from NothingUnpaidError, which is Pay's
// mirror-image refusal for a ticket that owes nothing: this one says the
// ticket is not finished, and names the four ways to finish it, because
// the bartender reading it on the floor has to pick one.
//
// A 409 rather than a 422: nothing about the request is malformed, the
// ticket is simply in a state this command is not permitted on, which is
// the same shape every other precondition refusal in this codebase uses.
export class OrderBalanceOutstandingError extends DomainError {
  /** Pre-tax cents still owed, so a caller can show the figure without
   * re-deriving it. */
  readonly outstandingCents: number;

  constructor(message: string, outstandingCents: number) {
    super(message, 409, "order_balance_outstanding");
    this.name = "OrderBalanceOutstandingError";
    this.outstandingCents = outstandingCents;
  }
}

// A3 (docs/decisions-awaiting-owner.md, ANSWERED 2026-08-29): "a zero
// balance does not close a tab while a tip is still unresolved", restoring
// PRD P0.9's "A paid check may remain Paid while an allowed tip adjustment
// is pending, then becomes Closed when final."
//
// Raised by src/domain/close.ts's closeOrder (CL1's own transaction, right
// beside OrderBalanceOutstandingError) when the balance is genuinely zero
// but nothing on this order settles the tip question: no captured payment
// exists (every captured payment already carries an explicit tip decision,
// even a first-class zero) and no order_tip_resolutions row exists either.
// An empty ticket (zero active lines) never reaches this: there is nobody
// to tip on a ticket nothing was ever rung on, the same reasoning
// closeIfSettled's own "no_active_lines" outcome already applies.
//
// A 409, the same shape as OrderBalanceOutstandingError: the request is
// not malformed, the ticket is simply not finished in the specific way
// this command cares about, and the message names the one action that
// resolves it (the floor's explicit no-tip action) because the bartender
// reading it has to know what to tap.
export class TipUnresolvedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "tip_unresolved");
    this.name = "TipUnresolvedError";
  }
}

// ALPHA-08: remove-line and change-liquor both target one order_line by
// id; this is raised when that line has already been removed (removed_at
// is not null). "Not found for this tenant" (NotFoundError, 404) still
// covers a line that never existed or belongs to another tenant; this is
// a meaningfully different, already-known state, so it gets its own 409
// rather than being folded into NotFoundError.
export class OrderLineRemovedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "order_line_removed");
    this.name = "OrderLineRemovedError";
  }
}

// ALPHA-12: "pay my drinks" is rejected when the request names a guestId
// that does not reference an active guest on this order (a real client
// bug, e.g. a stale guest id from a previous ticket), distinct from
// NotFoundError so the pay sheet can show "this guest is no longer on the
// ticket" rather than a generic 404.
export class GuestNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "guest_not_found");
    this.name = "GuestNotFoundError";
  }
}

// ALPHA-12: add-line is rejected when the order currently has two or more
// active guests and the request omits guestId, per the product's no-
// default philosophy (docs/product-requirements.md P0.19: "a drink added
// while multiple guests exist requires choosing whose it is"). Also raised
// when a guestId IS supplied but does not reference an active guest on
// THIS order (a stale or cross-order id). src/domain/guests.ts's
// resolveLineGuestId is the sole caller.
export class GuestSelectionRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 400, "guest_selection_required");
    this.name = "GuestSelectionRequiredError";
  }
}

// ALPHA-12: a whole-ticket or per-guest ("my drinks") payment is rejected
// while an equal-split plan is actively collecting on the same order
// (payment_split_plans.completed_at IS NULL). See src/domain/payments.ts's
// payOrder and src/domain/splitPayments.ts's own module header for the
// full race-avoidance write-up: only one money-moving flow may be "in
// flight" against an order's uncovered lines at a time. The bartender UI
// is expected to hide the other two pay modes once a split is in progress
// (the pay sheet's own "N of M shares collected" state already implies
// this), so reaching this error in practice means the UI's own state went
// stale, the same framing NothingUnpaidError's own comment uses.
export class SplitPaymentInProgressError extends DomainError {
  constructor(message: string) {
    super(message, 409, "split_payment_in_progress");
    this.name = "SplitPaymentInProgressError";
  }
}

// SPLIT1 (docs/build-briefs/SPLIT1-pay-by-guest-and-by-amount.md section 3.2). A pay-by-amount request that asks
// for more than the ticket still owes. Refused before anything is written and before the provider is called
// (PRD P0.8: a requested card allocation cannot exceed the ticket's unpaid balance; card overpayment is rejected).
// The message names the live balance so the floor can show it; the HTTP body also carries `remainingCents`.
export class AmountExceedsBalanceError extends DomainError {
  readonly remainingCents: number;
  constructor(message: string, remainingCents: number) {
    super(message, 409, "amount_exceeds_balance");
    this.name = "AmountExceedsBalanceError";
    this.remainingCents = remainingCents;
  }
}

// SPLIT1 (section 3.3). Once any pay-by-amount payment has captured on an order, a payment that names specific
// lines (By guest, a guest's no-guest row, Equal split, a walkout capture), and every command that could lower the
// ticket's total below what was already collected (remove, void, change liquor, price change, discount), is refused.
// Owner ruling 2026-10-10 (DEC-363 Q2): By guest and Equal show N/A for the rest of the ticket.
export class AmountPaymentsInProgressError extends DomainError {
  constructor(message: string) {
    super(message, 409, "amount_payments_in_progress");
    this.name = "AmountPaymentsInProgressError";
  }
}

// SPLIT1 review B1 and A3. The credits of a ticket's current generation of amount payments exceed what the ticket now
// totals in some component (a tax-rate change, a discount or a member price recomputing lower, a price edited below what
// was collected). Refused loudly before anything is charged: a negative remainder is never clamped to zero.
export class AmountCreditsExceedTicketError extends DomainError {
  constructor(message: string) {
    super(message, 409, "amount_credits_exceed_ticket");
    this.name = "AmountCreditsExceedTicketError";
  }
}

// SPLIT1 (owner ruling Q4, 2026-10-10). A refund of an amount payment that asks for more than the payment's refundable
// balance (captured less its tip less refunds already made).
export class RefundExceedsPaymentError extends DomainError {
  readonly refundableCents: number;
  constructor(message: string, refundableCents: number) {
    super(message, 409, "refund_exceeds_payment");
    this.name = "RefundExceedsPaymentError";
    this.refundableCents = refundableCents;
  }
}

// SPLIT1 (section 3.4). A refund is by line (the money rule) and an amount payment is not a claim on any line, so a
// refund aimed at one is refused before anything is written or the provider is called.
export class AmountPaymentNotRefundableByLineError extends DomainError {
  constructor(message: string) {
    super(message, 409, "amount_payment_not_refundable");
    this.name = "AmountPaymentNotRefundableByLineError";
  }
}

// ALPHA-12: a share can be collected (its own sequential simulated
// payment) at most once. Raised when a caller's precondition read finds
// the targeted payment_split_shares row already status = 'captured' AND
// the request is not a genuine replay of the SAME idempotency key (a
// replay returns the original result instead; see src/domain/
// splitPayments.ts's collectShare). A double-tap that minted a NEW
// idempotency key (rather than one being disabled/deduped client-side)
// is exactly the case this guards.
export class ShareAlreadyCollectedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "share_already_collected");
    this.name = "ShareAlreadyCollectedError";
  }
}

// Architect-gate correction (abandoned split deadlock, 2026-08-21): a split
// plan can be cancelled (src/domain/splitPayments.ts's cancelSplitPlan) only
// while it is genuinely active, i.e. neither completed (every share already
// captured) nor already cancelled. Raised when a caller's precondition read,
// taken under the plan row's own FOR UPDATE lock, finds either terminal
// state already set AND the request is not a genuine replay of the SAME
// idempotency key (a replay returns the original result instead). Distinct
// from ShareAlreadyCollectedError (which guards one share) and from
// NotFoundError (a plan id that never existed or belongs to another
// tenant), since "this plan already finished one way or the other" is a
// meaningfully different, already-known state that deserves its own 409.
export class SplitPlanNotActiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "split_plan_not_active");
    this.name = "SplitPlanNotActiveError";
  }
}

// Payments-track Slice 3 (terminal management): deactivateTerminal is
// rejected when the targeted terminal is already inactive AND the request
// is not a genuine replay of the SAME idempotency key (a replay returns
// the original result instead; see src/domain/terminals.ts). The same
// "already in this terminal state, distinct from NotFoundError" shape
// SplitPlanNotActiveError establishes for split plans.
export class TerminalAlreadyInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "terminal_already_inactive");
    this.name = "TerminalAlreadyInactiveError";
  }
}

// Payments-track Slice 3 (card-on-file): vaultCardFromCapture is rejected
// before any write when the request's own explicit-consent agreement is
// missing or not true. PROV-105 (docs/payment-provider-evaluation.md)
// requires "an explicit consent step"; the UI's own unchecked-by-default
// agreement checkbox is the first gate, this is the second, server-side
// one, per this codebase's "never trust the client blindly" discipline
// elsewhere (requireTipSelection's preset-membership check, the price-
// quote engine's own server-side re-pricing).
export class ConsentRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 400, "consent_required");
    this.name = "ConsentRequiredError";
  }
}

// Payments-track Slice 3 (card-on-file): vaultCardFromCapture is rejected
// when sourcePaymentAttemptId does not name a Captured payment attempt on
// the SAME order as the target guest -- vaulting only ever makes sense
// from a real card-present capture that just happened, per PROV-105's own
// "dip once, vault as a reusable token" flow, never an arbitrary or
// cross-order payment reference.
export class PaymentNotCapturedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_not_captured");
    this.name = "PaymentNotCapturedError";
  }
}

// Payments-track Slice 3 (card-on-file): a saved-card reference used in
// the pay flow ("Use saved card") or the remove-card command that does not
// resolve to an active, non-removed order_guest_saved_cards row for this
// tenant.
export class SavedCardNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "saved_card_not_found");
    this.name = "SavedCardNotFoundError";
  }
}

// VER34 (docs/build-briefs/VER34-provider-recheck-tests.md): a
// order_guest_saved_cards row's own provider_name (set once, at vault
// time, from whichever adapter vaulted it) no longer matches the
// adapter now configured to charge. Same shape as
// TerminalProviderMismatchError, for the same reason: reachable only
// after a provider cutover left a stale row behind. 409.
export class SavedCardProviderMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "saved_card_provider_mismatch");
    this.name = "SavedCardProviderMismatchError";
  }
}

// Payments-track Slice 4 (DEC-002, docs/decision-register.md). Raised
// when a caller asks to open a hold on a tab that either already has one
// (HoldAlreadyExistsError) or already has a card on file
// (HoldNotAllowedError, DEC-002 Section 1: "a guest with a card already
// vaulted on their profile... needs no hold at all"), and when a command
// targets a hold that is not currently active (HoldNotActiveError, e.g.
// already captured or released) or does not exist for this tenant/order
// (HoldNotFoundError). src/domain/tabs.ts is the sole raiser of all four.
export class HoldNotAllowedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "hold_not_allowed");
    this.name = "HoldNotAllowedError";
  }
}

export class HoldAlreadyExistsError extends DomainError {
  constructor(message: string) {
    super(message, 409, "hold_already_exists");
    this.name = "HoldAlreadyExistsError";
  }
}

export class HoldNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "hold_not_found");
    this.name = "HoldNotFoundError";
  }
}

export class HoldNotActiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "hold_not_active");
    this.name = "HoldNotActiveError";
  }
}

// Payments-track Slice 4: a held tab's actual total due, at Pay/Close,
// exceeds what is currently authorized on its hold (an exhausted
// increment ceiling, or a run of declined increments). Per DEC-002
// Section 3, this is never a silent write-off and never blocks the
// drinks already served: it surfaces as a manager exception requiring a
// second card or a fresh authorization, exactly as this error's own
// message states. src/domain/payments.ts's captureHeldTab raises this
// instead of ever capturing a lesser amount without saying so.
export class HoldCaptureExceedsAuthorizedError extends DomainError {
  // HOLDCLOSE1: the amount the capture needed, so the Pay route can raise the hold to cover it before refusing.
  readonly requiredCents: number | undefined;
  readonly authorizedCents: number | undefined;
  constructor(message: string, amounts?: { requiredCents: number; authorizedCents: number }) {
    super(message, 409, "hold_capture_exceeds_authorized");
    this.name = "HoldCaptureExceedsAuthorizedError";
    this.requiredCents = amounts?.requiredCents;
    this.authorizedCents = amounts?.authorizedCents;
  }
}

// PAY14 (docs/build-briefs/PAY14-order-hold-provider-recheck.md): an
// order_holds row's own provider_name (written once, when the hold was
// placed) no longer matches the adapter now configured. Same shape as
// TerminalProviderMismatchError and SavedCardProviderMismatchError, for
// the same reason: reachable only after a provider change left a hold
// behind. 409.
export class HoldProviderMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "hold_provider_mismatch");
    this.name = "HoldProviderMismatchError";
  }
}

// PAY14 (RC12, owner answer Q3 = B): Pay found a hold recorded under the
// simulated provider, set it aside, and stopped without charging, so the
// bartender knows a new card is needed. 409.
export class HoldClearedTakeNewCardError extends DomainError {
  constructor(message: string) {
    super(message, 409, "hold_cleared_take_new_card");
    this.name = "HoldClearedTakeNewCardError";
  }
}

// Architect gate correction, 2026-08-25 (Findings 2/3, provider-success /
// local-record boundary): raised by src/domain/payments.ts's payOrder and
// src/domain/splitPayments.ts's collectShare, BEFORE any adapter call is
// made, whenever this order already carries a payment_attempts row still
// sitting in the new non-terminal "Pending" status (a prior attempt whose
// provider outcome was CapturePending, AwaitingCard, or any other
// genuinely non-terminal/unknown status -- see chargeThroughAdapter's own
// header for the full three-way status mapping). Blocks EVERY further Pay
// path against this order (whole-ticket, per-guest, a saved card, or an
// active hold's own capture) rather than only the one that produced the
// Pending attempt: the owner ruling (docs/decision-register.md) is
// "block-and-flag," never a silent second capture. A stuck Pending
// attempt is meant to surface for MANUAL reconciliation (a manager
// confirms the real outcome with the provider and either records it as
// Captured or Failed); no automated resolution path exists in this slice
// (see the payments-track README's own cutover notes for that follow-up
// item). This is the mechanism that makes a fresh idempotency key unable
// to re-attempt a charge that might already have succeeded: without it, a
// new domain idempotency key produces a brand-new adapter-level key
// (deriveAdapterIntentIdempotencyKey and friends are keyed off the DOMAIN
// key), which the provider's own idempotency cache has never seen before
// and will happily charge again.
export class PaymentPendingReconciliationError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_pending_reconciliation");
    this.name = "PaymentPendingReconciliationError";
  }
}

// Terminal-assignment slice (docs/solution-architecture.md Section 8.3,
// realized): src/domain/payments.ts's resolveTerminalReferenceForCharge
// raises this when a real reader-backed provider (anything other than
// "simulated") is asked to charge with no terminalId at all. The
// documented per-tenant placeholder (defaultTerminalReference) names no
// real reader, so sending it to a real provider would either 500 or
// (worse) silently target whatever reader the placeholder string happens
// to resolve to; this fails loud instead, before any adapter call, naming
// the fix in plain language. The simulated provider is exempt (see
// resolveTerminalReferenceForCharge's own comment for the documented
// asymmetry and why it is safe): every production resolution is
// "simulated" today (src/config/payments.ts), so this is only reachable
// by a caller that explicitly injects a non-simulated adapter.
export class TerminalNotAssignedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "terminal_not_assigned");
    this.name = "TerminalNotAssignedError";
  }
}

// PAY6 (docs/build-briefs/PAY6-terminal-provider-mismatch.md): a
// registered payment_terminals row's own provider_name (set once, at
// registerTerminal time, from whichever adapter registered it) no longer
// matches the adapter now configured to charge. Reachable only when a
// tenant's configured provider changed after a terminal was registered
// under the old one (the 2026-08-25 simulated-to-Stripe cutover on the
// stage0 demo tenant is the first real instance) and nothing since has
// retired the stale row. 409, matching TerminalNotAssignedError's own
// shape immediately above, since this is also "the charge cannot proceed
// as configured," not "the referenced terminal does not exist" (that
// stays PaymentTerminalNotFoundError, a 404).
export class TerminalProviderMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "terminal_provider_mismatch");
    this.name = "TerminalProviderMismatchError";
  }
}

// PAY1-S4-F1: a reader belongs to the account that registered it. A missing
// historical stamp or a changed mapping needs a newly registered reader.
export class TerminalMerchantAccountMismatchError extends DomainError {
  constructor() {
    super(
      "This reader belongs to another payment account. Register a reader for this location's current account in Settings.",
      409,
      "terminal_merchant_account_mismatch",
    );
    this.name = "TerminalMerchantAccountMismatchError";
  }
}

// PAY-LOC1 (docs/backlog.md). Thrown by
// src/domain/paymentTerminalLocations.ts's ensureTerminalLocationForProvider
// BEFORE any adapter call, when the configured provider needs a real
// mailing address for its own Terminal Location concept (Stripe requires
// line1, city, state, postal_code, and country for a US address; see
// docs.stripe.com/terminal/fleet/locations-and-zones) and the app's own
// location has no complete address on file yet. 409, the same "cannot
// proceed as configured right now" shape TerminalNotAssignedError and
// TerminalProviderMismatchError already use: this is a real, fixable
// setup gap, not a malformed request (400) or a missing entity (404).
export class LocationAddressIncompleteError extends DomainError {
  constructor(message: string) {
    super(message, 409, "location_address_incomplete");
    this.name = "LocationAddressIncompleteError";
  }
}

// TERM2 (docs/build-briefs/TERM2-physical-reader-registration.md). The
// payment provider refused to register a reader, translated from
// src/adapters/payments/interface.ts's TerminalRegistrationRejectedError.
// The message is fixed plain wording chosen by the domain; it never carries
// the pairing code or the provider's own text. 422 for a code the provider
// will not take, 409 for a reader that is already registered, 502 when the
// provider refused for a reason this product does not name.
export class TerminalRegistrationRefusedError extends DomainError {
  constructor(reason: "invalid_code" | "already_registered" | "provider_refused", message: string) {
    super(
      message,
      reason === "invalid_code" ? 422 : reason === "already_registered" ? 409 : 502,
      reason === "invalid_code"
        ? "terminal_registration_invalid_code"
        : reason === "already_registered"
          ? "terminal_already_registered"
          : "terminal_registration_failed",
    );
    this.name = "TerminalRegistrationRefusedError";
  }
}

// PAY-LOC1-F1 (docs/backlog.md). Thrown by
// src/domain/paymentTerminalLocations.ts's
// updateLocationAddressWithProviderSync BEFORE any write at all (neither
// the local `locations` row nor the provider), when a request would
// change a location's `country` and a payment_terminal_locations row
// already exists for that location and the configured non-simulated
// provider. Stripe's own documented rule: "After you create a location,
// you can't change its country" (docs.stripe.com/terminal/fleet/
// locations-and-zones). 409, the same "cannot proceed as requested right
// now" shape LocationAddressIncompleteError already uses just above.
export class TerminalLocationCountryImmutableError extends DomainError {
  constructor(message: string) {
    super(message, 409, "terminal_location_country_immutable");
    this.name = "TerminalLocationCountryImmutableError";
  }
}

// Terminal-assignment slice: a caller-supplied terminalId does not
// resolve to an ACTIVE payment_terminals row for this tenant -- never
// registered, registered for a different tenant, or deactivated since a
// device's terminal-setup picker last stored it (a terminal picked at
// setup can go stale if a manager deactivates it later from Settings).
// 404, the same "referenced entity not found for this tenant" shape
// SavedCardNotFoundError/GuestNotFoundError already establish elsewhere
// in this file. Distinct from TerminalNotAssignedError above (that one
// means "nothing was ever chosen"; this one means "something was chosen
// and it is no longer good"), so the UI can tell the two apart and send a
// bartender back to setup with the right message either way.
export class PaymentTerminalNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "payment_terminal_not_found");
    this.name = "PaymentTerminalNotFoundError";
  }
}

// PAY7 (docs/build-briefs/PAY7-pay-retry-after-terminal-change.md): the
// domain-level translation of src/adapters/payments/interface.ts's
// TerminalPresentmentConflictError, thrown when a Pay/collectShare/hold-open
// retry under the same domain idempotency key resolves to a DIFFERENT
// terminal than an earlier attempt already presented the same underlying
// provider intent to, and that earlier presentment has not resolved to a
// definite outcome this call could report instead. 409: the charge cannot
// proceed as configured RIGHT NOW, the same taxonomy TerminalNotAssignedError/
// TerminalProviderMismatchError already use for "try again once you fix or
// wait out X," never "this thing does not exist" (404) or "this input is
// malformed" (400). See ui/src/api.ts's shouldClearPayIdempotencyKeyOnFailure
// for why this ONE 409 is a deliberate, named exception to that function's
// own "every non-5xx DomainError is safe to clear the key on" rule.
export class PaymentRetryConflictError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_retry_conflict");
    this.name = "PaymentRetryConflictError";
  }
}

// PAY32 (docs/build-briefs/PAY32-reader-busy.md): the domain-level translation
// of src/adapters/payments/interface.ts's TerminalReaderBusyError, thrown by
// chargeThroughAdapter when the provider refused to start a presentment
// because the reader is processing another payment (Stripe's
// terminal_reader_busy). A DEFINITE "not charged" outcome: the refused call
// put nothing on the reader, so the money-path transaction rolls back and no
// payment attempt (and so nothing Pending) is left behind. 409, the same
// "try again once the thing you are waiting on clears" taxonomy as
// PaymentRetryConflictError, and, unlike that one, safe for the client to
// retry under a fresh key.
export class PaymentReaderBusyError extends DomainError {
  constructor() {
    super(
      "This card reader is busy with another payment. Wait for it to finish and try again.",
      409,
      "terminal_reader_busy",
    );
    this.name = "PaymentReaderBusyError";
  }
}

// TERM6 (docs/build-briefs/TERM6-reader-offline-and-action-failed.md): the domain-level translation of
// src/adapters/payments/interface.ts's TerminalReaderOfflineError, raised by chargeThroughAdapter
// (src/domain/payments.ts). A DEFINITE "not charged" outcome: the adapter cancelled the intent it created. The
// money-path transaction rolls back, so no payment attempt is left behind. 409, non-5xx, so the floor mints a fresh Pay key on the next tap.
export class PaymentReaderOfflineError extends DomainError {
  constructor() {
    super(
      "This card reader is offline. Nothing was charged. Check that it is powered on and connected to the internet, then try again.",
      409,
      "terminal_reader_offline",
    );
    this.name = "PaymentReaderOfflineError";
  }
}

// LIVETEST1 (DEC-600). A live test sale is a real $1.00 charge, so a second one is not started while another at the location is
// still open (waiting on the reader, or charged with the refund not confirmed). Refused before any provider call.
export class CardTestSaleInFlightError extends DomainError {
  constructor() {
    super(
      "Another test sale is still open. Wait for it to finish, then try again.",
      409,
      "card_test_sale_in_flight",
    );
    this.name = "CardTestSaleInFlightError";
  }
}

// LIVETEST1 (DEC-600). An earlier live test sale was charged and its refund was not confirmed, and a manager has not resolved the
// notice. Another $1.00 charge on top of it is not started.
export class CardTestSaleRefundOpenError extends DomainError {
  constructor() {
    super(
      "An earlier $1.00 test charge was not refunded. Refund it from your Stripe dashboard, have a manager resolve it under Manager exceptions, then try again.",
      409,
      "card_test_sale_refund_open",
    );
    this.name = "CardTestSaleRefundOpenError";
  }
}

// ONB4-S2. The provider does not report the chosen reader online, so no charge is started.
export class CardTestSaleReaderNotOnlineError extends DomainError {
  constructor() {
    super(
      "The card reader is not online. Check that it is on and connected, then try again.",
      409,
      "card_test_sale_reader_not_online",
    );
    this.name = "CardTestSaleReaderNotOnlineError";
  }
}

// TAX2-S1-F3 (docs/build-briefs/TAX2-S1-F3-captured-amount-and-reader-tip.md). The provider reports a captured
// amount that differs from the amount requested, and it did not report an explicit tip that accounts for the
// whole difference (higher with no tip, or lower, such as a partial authorization). Unlike the refusals above
// this is NOT a definite "not charged": the card was charged. The payment row is not written (the stored
// subtotal + tax + tip would no longer equal the amount, and the parts are never adjusted to fit), and the
// caller records a manager exception of the existing kind unmatched_provider_charge before this is thrown to
// the client. 409. The message names the one safe action; it carries no amount and no provider value, and the
// structured fields below are for the recording step only, never for a response body or a log.
export class CapturedPaymentReconciliationRequiredError extends DomainError {
  constructor() {
    super(
      "Payment needs reconciliation. Do not charge the card again.",
      409,
      "captured_payment_reconciliation_required",
    );
    this.name = "CapturedPaymentReconciliationRequiredError";
  }
}

export class CapturedAmountMismatchError extends DomainError {
  readonly orderId: string;
  readonly providerReference: string;
  readonly requestedAmountCents: number;
  readonly capturedAmountCents: number;

  constructor(facts: {
    orderId: string;
    providerReference: string;
    requestedAmountCents: number;
    capturedAmountCents: number;
  }) {
    super(
      "The card was charged for a different amount than this payment asked for, so it was not recorded. " +
        "Do not charge the card again. A manager has been alerted to reconcile it.",
      409,
      "captured_amount_mismatch",
    );
    this.name = "CapturedAmountMismatchError";
    this.orderId = facts.orderId;
    this.providerReference = facts.providerReference;
    this.requestedAmountCents = facts.requestedAmountCents;
    this.capturedAmountCents = facts.capturedAmountCents;
  }
}

// TAX2-S1-F3. The same situation as CapturedAmountMismatchError, but the manager exception row could not be
// written even after one retry, so this message does NOT claim a manager was alerted; it tells the person to
// call one. The nightly PAY10 pass still finds the charge. 409, its own code so the Pay screen can say so.
export class CapturedAmountMismatchNotAlertedError extends DomainError {
  readonly orderId: string;
  readonly providerReference: string;
  readonly requestedAmountCents: number;
  readonly capturedAmountCents: number;

  constructor(facts: {
    orderId: string;
    providerReference: string;
    requestedAmountCents: number;
    capturedAmountCents: number;
  }) {
    super(
      "The card was charged for a different amount than this payment asked for, so it was not recorded. " +
        "Do not charge the card again. Tell a manager now so they can reconcile it.",
      409,
      "captured_amount_mismatch_not_alerted",
    );
    this.name = "CapturedAmountMismatchNotAlertedError";
    this.orderId = facts.orderId;
    this.providerReference = facts.providerReference;
    this.requestedAmountCents = facts.requestedAmountCents;
    this.capturedAmountCents = facts.capturedAmountCents;
  }
}

// PL3-F2 (DEC-332). The provider reports a payment link paid, the domain permanently refused to record it, and
// the manager exception that should have been written at once could not be (after one retry). A 500 class error
// on purpose: the push handler rethrows it so the webhook event stays NON-final and Stripe redelivers, and a
// poll repeats the whole attempt. The message names no amount and no provider value.
export class PaymentLinkPaidNotAlertedError extends DomainError {
  constructor() {
    super(
      "A payment link was paid but could not be recorded, and a manager could not be alerted yet. " +
        "It will be retried; tell a manager now.",
      500,
      "payment_link_paid_not_alerted",
    );
    this.name = "PaymentLinkPaidNotAlertedError";
  }
}

// CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-281,
// DEC-286). Live card money is refused while the location's time zone and
// cutoff are still the unconfirmed defaults. A DEFINITE "not charged": the
// gate (src/domain/liveMoneyGate.ts) runs before any provider call and
// before any write, so the refusal leaves nothing behind. 409, the same
// "retry once the thing you are waiting on changes" taxonomy as
// PaymentReaderBusyError; the message names what to set and where.
export class BusinessDayNotConfirmedError extends DomainError {
  constructor() {
    super(
      "Card payments are turned off until the time zone and closing time are confirmed. " +
        "Ask a manager to confirm them in Settings, under Location.",
      409,
      "business_day_not_confirmed",
    );
    this.name = "BusinessDayNotConfirmedError";
  }
}

// ONB1: the sales tax rate twin of BusinessDayNotConfirmedError. Live card money waits for the admin to confirm (or
// record) the rate. 409, same taxonomy; the message names what to do and where.
export class TaxRateNotConfirmedError extends DomainError {
  constructor() {
    super(
      "Card payments are turned off until the sales tax rate is confirmed. " +
        "Ask an admin to confirm it in Settings, under Money and pricing.",
      409,
      "tax_rate_not_confirmed",
    );
    this.name = "TaxRateNotConfirmedError";
  }
}

// Inventory track. A count session is a two-state machine (open, then
// closed once, never reopened): recordCount and closeCountSession both
// reject a session that is not currently open. Mirrors OrderClosedError's
// shape exactly (a real, already-known state, not a 404).
export class CountSessionNotOpenError extends DomainError {
  constructor(message: string) {
    super(message, 409, "count_session_not_open");
    this.name = "CountSessionNotOpenError";
  }
}

// INV2 (docs/backlog.md, PRD P0.7): "Each location count session has one
// assigned counting employee." recordCount raises this when the caller's
// recordedByEmployeeId does not match the session's current
// assigned_counter_employee_id: a 403, the same class InsufficientRightsError
// uses, since this is an authorization refusal (this specific person is not
// the one assigned to this specific session) rather than a not-found or a
// state conflict.
export class CountSessionWrongCounterError extends DomainError {
  constructor(message: string) {
    super(message, 403, "count_session_wrong_counter");
    this.name = "CountSessionWrongCounterError";
  }
}

// Auth track (track/auth). No Authorization header, an unknown/expired
// token, or a session past its tenant's configured inactivity timeout
// (src/domain/users.ts's validateSession). Deliberately distinct from
// InvalidPinCredentialsError below: this means "you are not signed in at
// all right now", not "the PIN you typed was wrong".
export class AuthenticationRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 401, "authentication_required");
    this.name = "AuthenticationRequiredError";
  }
}

// Auth track: loginWithPin's user-id-plus-PIN combination did not verify,
// OR changePin's supplied oldPin did not verify. A single generic message
// on both ("incorrect PIN") is deliberate: the tile picker already
// discloses which display names exist on this tenant, so there is no
// meaningful "does this user exist" secret to protect, but which SPECIFIC
// PIN attempt or byte failed is never worth distinguishing in the
// response.
export class InvalidPinCredentialsError extends DomainError {
  constructor(message: string) {
    super(message, 401, "invalid_pin_credentials");
    this.name = "InvalidPinCredentialsError";
  }
}

/** TEN9-F15 (provisional): a temporary PIN issued by a Software Owner reset was not used within 24 hours. */
export class TemporaryPinExpiredError extends DomainError {
  constructor() {
    super("this temporary PIN has expired; ask for a new reset", 401, "temporary_pin_expired");
    this.name = "TemporaryPinExpiredError";
  }
}

/** TEN9-F15: a customer admin was reset too many times in the last hour. */
export class CustomerAdminResetLimitError extends DomainError {
  constructor(limit: number) {
    super(
      `This account was already reset ${limit} times in the last hour. Wait before trying again.`,
      429,
      "reset_limit",
    );
    this.name = "CustomerAdminResetLimitError";
  }
}

// Auth track: loginWithPin is rejected while
// users.locked_until is in the future (pin-policy-proposal.md item 2: 5
// failed attempts locks the user for 5 minutes). 423 Locked, not 401,
// since the credential itself may well be correct; the account is simply
// not accepting logins right now. Message names the remaining lockout
// time in plain language; never the PIN, never the hash.
export class AccountLockedError extends DomainError {
  constructor(message: string) {
    super(message, 423, "account_locked");
    this.name = "AccountLockedError";
  }
}

// Auth track: a real, authenticated, non-manager user attempted a
// manager-only settings route. Distinct from AuthenticationRequiredError
// (that one means "not signed in"; this one means "signed in, but not
// allowed to do this").
export class InsufficientRightsError extends DomainError {
  constructor(message: string) {
    super(message, 403, "insufficient_rights");
    this.name = "InsufficientRightsError";
  }
}

// Auth track: deactivateUser is rejected when the targeted user is
// already inactive AND the request is not a genuine replay of the SAME
// idempotency key, the identical shape TerminalAlreadyInactiveError
// establishes for terminals.
export class UserAlreadyInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "user_already_inactive");
    this.name = "UserAlreadyInactiveError";
  }
}

// U6 (2026-09-02, DEC-041 item 4): reactivateUser's own mirror of
// UserAlreadyInactiveError immediately above -- rejected when the
// targeted user is already active AND the request is not a genuine
// replay of the SAME idempotency key.
export class UserAlreadyActiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "user_already_active");
    this.name = "UserAlreadyActiveError";
  }
}

// Auth track: unlockUser is rejected when the targeted user is not
// currently locked (locked_until is null or already in the past) AND the
// request is not a genuine replay of the SAME idempotency key. Nothing to
// unlock is a real, already-known state, not a 404.
export class UserNotLockedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "user_not_locked");
    this.name = "UserNotLockedError";
  }
}

// Auth track, gate correction (Finding 1, HIGH): a session whose user
// carries must_change_pin = true is authenticated but not yet entitled to
// use the product. src/domain/users.ts's requireAuthenticatedUser raises
// this for every route except the small allowlist that route itself
// documents (the PIN-change route and session introspection); every
// route reached through requirePermission (which composes
// requireAuthenticatedUser with no allowlist override) is included, so a
// mustChangePin user, including the bootstrap manager still on the
// documented well-known PIN, cannot reach a single manager-gated action
// before completing the forced change. Distinct from
// InsufficientRightsError (that one means "signed in, wrong role"; this
// one means "signed in, right role, but this account has a mandatory step
// left before it may act at all").
export class PinChangeRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 403, "pin_change_required");
    this.name = "PinChangeRequiredError";
  }
}

// Gate finding 8, 2026-08-27: the SECOND person in a manager-approval
// override (src/domain/users.ts's verifyApproverPin) has never signed in
// and still carries the one-time temporary PIN an admin was handed at
// account creation. A temporary PIN is a hand-off credential, not an
// authorization credential, so it must not approve a money or inventory
// action -- the gate's own live happy path approved a tab reopen with
// one. Distinct from PinChangeRequiredError, which is about the ACTING
// session's own mandatory step and is what src/api/server.ts's
// requireAuthenticatedUser raises: this one is about a THIRD party whose
// PIN was just presented, and the acting session is perfectly entitled to
// keep working and pick a different manager. ui/src/approvalFlow.ts's
// classifyApprovalRetryResult maps this code to "return_to_picker" for
// exactly that reason.
export class ApproverPinChangeRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 403, "approver_pin_change_required");
    this.name = "ApproverPinChangeRequiredError";
  }
}

// Added post-integration-merge-2 (owner-triggered, 2026-08-23): Cancel
// (src/domain/orderCancel.ts) is the "void an empty ticket" command,
// deliberately narrower than Close. It is refused, before writing
// anything, on an order that already carries a line (even a removed one)
// or a payment attempt (even a failed one): "empty" means nothing was ever
// added, not merely nothing currently active, matching the UI's own
// "Void ticket" affordance being offered only for an untouched draft
// ticket. Named separately from OrderAlreadyPaidError/OrderClosedError
// because this is a precondition on order CONTENTS, not on order STATE.
export class OrderNotEmptyError extends DomainError {
  constructor(message: string) {
    super(message, 409, "order_not_empty");
    this.name = "OrderNotEmptyError";
  }
}

// M0 (docs/backlog.md, owner ruling 2026-08-27). Removing a line and
// voiding a line are now two different commands, split on one fact: was
// the drink actually poured. POST .../order-lines/:id/remove refuses a
// line whose prepared_state is 'prepared', because that drink left the
// bottle and taking it off the ticket is a money correction that must
// say what happened. The caller's fix is POST .../order-lines/:id/void
// with a reason, which is a different permission (money.line.void_prepared)
// and, for the Assistant Manager tier, a manager-approved one. 409 and
// its own code so the floor UI can tell this apart from a line that was
// already removed and route the person to the void picker instead of
// showing a dead end.
export class LineAlreadyPreparedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "line_already_prepared");
    this.name = "LineAlreadyPreparedError";
  }
}

// M0, the mirror of the above: POST .../order-lines/:id/void refuses a
// line that was never Prepared. Nothing was poured, so there is nothing
// to attribute and no reason to demand; the caller wants
// POST .../order-lines/:id/remove, which needs neither a reason nor a
// manager. Kept as its own code rather than folded into a generic
// validation error so the UI can say exactly that.
export class LineNotPreparedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "line_not_prepared");
    this.name = "LineNotPreparedError";
  }
}

// M0. The reason named on a void exists for this tenant but has been
// deactivated, so it is no longer offered on the picker and cannot be
// chosen for a NEW void. It stays perfectly readable on every historical
// void that already references it, which is the whole point of
// deactivating rather than deleting. Almost always a stale client: the
// picker was loaded before an Admin retired that reason. 409, so the UI
// reloads the list and asks again rather than treating it as a 404 on
// the line.
export class VoidReasonInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "void_reason_inactive");
    this.name = "VoidReasonInactiveError";
  }
}

// Independent-review Finding 6: the network-wide client directory search
// endpoint (POST /tenants/:tenantId/clients/search) had no rate limiting,
// so an authenticated actor could script full-directory enumeration.
// src/domain/clientSearchThrottle.ts's checkAndRecordClientSearchThrottle
// raises this, before the search query runs, once either the per-minute
// or the per-day counter for the acting user exceeds its configured
// limit (src/config/clientSearchThrottle.ts). 429, not 409: this is a
// rate-limit refusal the caller should back off and retry later, not a
// conflicting-state error about the resource itself.
export class ClientSearchRateLimitExceededError extends DomainError {
  constructor(message: string) {
    super(message, 429, "client_search_rate_limit_exceeded");
    this.name = "ClientSearchRateLimitExceededError";
  }
}

// Second-review Finding N3: the request could not get through because
// something else in this tenant was holding the user-administration lock,
// or the process could not get a pooled connection in time. Nothing is
// wrong with the request and nothing was written; the caller should try
// again in a moment.
//
// 503 with a retryable code, deliberately, and never a 500. A lock wait
// that times out is a capacity answer, not a failure of the server to
// work: a 500 would tell an operator to go looking for a bug, tell the UI
// to show "unexpected server error," and tell every retry policy in front
// of the service not to bother. It is also never a 409: nothing about the
// requested state conflicts, so a caller replaying the identical request
// unchanged is exactly the right response.
export class ServiceContentionError extends DomainError {
  constructor(message: string) {
    super(message, 503, "service_busy");
    this.name = "ServiceContentionError";
  }
}

/** PostgreSQL SQLSTATEs the two SET LOCAL timeouts in src/domain/
 * tenantAdministrationLock.ts produce, plus the deadlock code for
 * completeness. 55P03 is lock_not_available (lock_timeout expired),
 * 57014 is query_canceled (statement_timeout expired), 40P01 is
 * deadlock_detected. */
const CONTENTION_SQLSTATES = new Set(["55P03", "57014", "40P01"]);

/**
 * Translates the three ways contention reaches this process into one
 * retryable domain error, or returns undefined when the error is
 * something else entirely and must keep propagating.
 *
 * Called from src/api/server.ts's error handler rather than from each
 * write path, so it covers all three shapes uniformly, including the one
 * no route can catch for itself:
 *
 *   1. lock_timeout fired while waiting for the tenant's user-
 *      administration advisory lock (SQLSTATE 55P03).
 *   2. statement_timeout fired inside that same transaction (57014).
 *   3. The pool could not hand out a connection within
 *      connectionTimeoutMillis. node-postgres raises a plain Error with
 *      no SQLSTATE for this one, so it is matched on its message, which
 *      is a fixed string in the driver. Matched narrowly and case-
 *      insensitively; a driver message change would make this stop
 *      matching and fall through to the 500 it produces today, which is
 *      the safe direction for a match on a string.
 */
export function asContentionError(error: unknown): ServiceContentionError | undefined {
  if (error instanceof ServiceContentionError) {
    return error;
  }
  if (typeof error === "object" && error !== null) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && CONTENTION_SQLSTATES.has(code)) {
      return new ServiceContentionError(
        "someone else is changing this location's users right now. Try again in a moment.",
      );
    }
    const message = (error as { message?: unknown }).message;
    if (
      typeof message === "string" &&
      message.toLowerCase().includes("timeout exceeded when trying to connect")
    ) {
      return new ServiceContentionError(
        "the system is busy right now and could not take this request. Try again in a moment.",
      );
    }
  }
  return undefined;
}

// OPS7 (docs/build-briefs/OPS7-database-unreachable-is-503.md). The database could not be reached at all: it is
// stopped, still starting, refusing connections or out of them. Nothing about the request is wrong and nothing was
// written, so it is a 503 with a stable code, never the 500 "unexpected server error" a real bug gets. The message is
// the exact sentence every screen shows (ui/src/serviceUnavailable.ts holds the same text).
export const SERVICE_UNAVAILABLE_MESSAGE = "Can't reach the server. Try again in a minute.";

export class ServiceUnavailableError extends DomainError {
  constructor(message: string = SERVICE_UNAVAILABLE_MESSAGE) {
    super(message, 503, "service_unavailable");
    this.name = "ServiceUnavailableError";
  }
}

/** Connect-level Node error codes. Read from the error itself and never from a nested `cause`, so a payment
 * provider's failed fetch (a TypeError whose cause carries the code) is not mistaken for the database. node-postgres
 * raises these on the error object (an AggregateError for a refused dual-stack connect carries `code` too). */
const DATABASE_UNREACHABLE_NODE_CODES = new Set([
  "ECONNREFUSED",
  "ETIMEDOUT",
  "EHOSTUNREACH",
  "ENETUNREACH",
  "ENOTFOUND",
  "EAI_AGAIN",
]);

/** SQLSTATEs for a server that cannot take a connection: 53300 too_many_connections, 57P03 cannot_connect_now
 * (starting up), 57P01 admin_shutdown, 57P02 crash_shutdown, 08001 / 08004 / 08006 connection failures. */
const DATABASE_UNREACHABLE_SQLSTATES = new Set([
  "53300",
  "57P03",
  "57P01",
  "57P02",
  "08001",
  "08004",
  "08006",
]);

/** node-postgres's own fixed messages, matched narrowly and case-insensitively. A driver wording change stops the
 * match and the error falls through to the 500 it produced before, the safe direction for a string match. */
const DATABASE_UNREACHABLE_MESSAGES = [
  "connection terminated due to connection timeout",
  "connection terminated unexpectedly",
  "the database system is starting up",
  "sorry, too many clients already",
];

/** Translates "the database cannot be reached" into one retryable domain error, or returns undefined for anything
 * else so a real bug keeps its 500. Called from the one error handler in src/api/server.ts, not from each route. */
export function asDatabaseUnreachableError(error: unknown): ServiceUnavailableError | undefined {
  if (error instanceof ServiceUnavailableError) {
    return error;
  }
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const code = (error as { code?: unknown }).code;
  if (
    typeof code === "string" &&
    (DATABASE_UNREACHABLE_NODE_CODES.has(code) || DATABASE_UNREACHABLE_SQLSTATES.has(code))
  ) {
    return new ServiceUnavailableError();
  }
  const message = (error as { message?: unknown }).message;
  if (typeof message === "string") {
    const lowered = message.toLowerCase();
    if (DATABASE_UNREACHABLE_MESSAGES.some((known) => lowered.includes(known))) {
      return new ServiceUnavailableError();
    }
  }
  return undefined;
}

export function isDomainError(error: unknown): error is DomainError {
  return error instanceof DomainError;
}

// F7-W1 (docs/backlog.md; stage0 bug 62f91c59). ITEM2-F7's write
// guards (migrations 0132/0133/0141: item2_f7_guard_inventory,
// item2_f7_guard_count, item2_f7_guard_product, item2_f7_guard_kind,
// item2_f7_guard_policy, item2_f7_guard_pour) each RAISE EXCEPTION with
// SQLSTATE 23514 (check_violation) when a write conflicts with a
// tenant's cutover state or a product's locked canonical unit. Every
// application write path this session found reachable from an ordinary
// route is now converted or refused BEFORE the database (see
// src/domain/inventoryUnitConversion.ts), but the guard itself must never
// be weakened, and a route that still reaches it (a fenced tenant, a
// caller that bypasses domain validation, a future write path this sweep
// missed) used to surface as this error handler's generic 500
// "unexpected server error" -- exactly the failure mode CLAUDE.md's own
// "never let a failure render as silence" rule exists to close. Mapped
// here, at the one chokepoint src/api/server.ts's error handler already
// uses for asContentionError, to a clean 409 naming the guard's own
// plain-English message; the underlying error is still logged in full at
// the call site (app.log.error, unchanged) so nothing about the refusal
// is quiet in the logs, only in what a manager sees.
//
// Matched narrowly, by message substring, not by SQLSTATE alone: 23514
// covers every CHECK constraint in this schema, most of which have
// nothing to do with ITEM2-F7 and already return a sensible refusal
// through their own domain-layer validation before ever reaching the
// database. The substrings below are the STABLE part of each guard's own
// RAISE EXCEPTION text (the part before a `%` placeholder Postgres fills
// with a tenant id, product id or value), taken verbatim from
// db/migrations/0132_item2_f7_fenced_product_cutover.sql,
// 0133_item2_f7_kind_policy_enforcement.sql and
// 0141_item2_f7_s6_legacy_null_product_reversal.sql. Scoped to the
// guards an ordinary application write can actually reach (fencing,
// canonical-unit enforcement, policy/pour append-only and narrowing
// rules); the cutover/policy-activation RPCs' OWN guards (fleet
// attestation, replay-fact mismatches, the all-tenant audit) are never
// reached through a normal route and are deliberately left unmatched, so
// an operator driving those RPCs directly still sees the raw database
// message a cutover tool needs.
//
// Each pattern carries its OWN fixed, plain-language reply, never the
// raw Postgres text: that text interpolates a `%` placeholder with a
// tenant id or product id (a real UUID), which is exactly what CLAUDE.md's
// privacy rule and ordinary manager-facing copy both refuse to put in
// front of a person. The raw error (ids, table names, everything) is
// still logged in FULL at the call site (src/api/server.ts's
// app.log.warn(error, ...), unchanged) -- nothing about the refusal is
// quiet in the logs, only in what a manager sees on screen.
const ITEM2_F7_GUARD_REPLIES: ReadonlyArray<{ readonly pattern: RegExp; readonly reply: string }> =
  [
    {
      pattern: /is fenced for inventory cutover/,
      reply:
        "This location's inventory is locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /is fenced; count work is unavailable/,
      reply: "Counting is locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /is fenced; catalog edits are unavailable/,
      reply: "Catalog edits are locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /is fenced; vocabulary edits are unavailable/,
      reply: "Settings edits are locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /is fenced; policy evidence cannot change/,
      reply: "Policy settings are locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /is fenced; pour evidence cannot change/,
      reply:
        "Pour size settings are locked for a data cutover right now. Try again in a few minutes.",
    },
    {
      pattern: /unit conversion legs require the fenced cutover writer/,
      reply: "This action is reserved for the inventory cutover process and can't be done here.",
    },
    {
      pattern: /unit conversion legs cannot be individually reversed/,
      reply: "This entry is part of a unit conversion and can't be reversed by itself.",
    },
    {
      pattern: /requires a product on every new quantity row/,
      reply: "Pick a real catalog item before saving this entry.",
    },
    {
      pattern: /quantity row product is missing or from another tenant/,
      reply: "That item could not be found for this location.",
    },
    {
      pattern: /requires canonical unit/,
      reply: "This item has to be recorded in ounces now. Check its bottle size in Stock & vendor.",
    },
    {
      pattern: /product mechanism lock is monotonic/,
      reply: "This item's selling method is locked and can't be changed back.",
    },
    {
      pattern: /legacy item-kind mechanism cannot reinterpret complete products/,
      reply: "This item kind's selling method can't be changed for this location anymore.",
    },
    {
      pattern: /policy evidence is append-only/,
      reply: "This policy history can't be edited or removed, only added to.",
    },
    {
      pattern: /policy enrollment requires a product write/,
      reply: "This item kind's selling policy has to be set from an item, not directly.",
    },
    {
      pattern: /pour evidence is append-only/,
      reply: "Pour size history can't be edited or removed, only added to.",
    },
    {
      pattern: /fixed item-kind policies cannot be changed/,
      reply: "This item kind's selling method is fixed and can't be changed.",
    },
    {
      pattern: /custom policies are replaced as a complete set/,
      reply: "This item kind's selling methods have to be saved as a complete set.",
    },
    {
      pattern: /policy narrowing conflicts with product/,
      reply: "That selling method is still used by an item and can't be removed.",
    },
  ];

export class Item2F7GuardViolationError extends DomainError {
  constructor(message: string) {
    super(message, 409, "inventory_cutover_guard");
    this.name = "Item2F7GuardViolationError";
  }
}

export function asItem2F7GuardError(error: unknown): Item2F7GuardViolationError | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  if (code !== "23514") return undefined;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return undefined;
  const matched = ITEM2_F7_GUARD_REPLIES.find(({ pattern }) => pattern.test(message));
  if (matched === undefined) return undefined;
  return new Item2F7GuardViolationError(matched.reply);
}

// ---------------------------------------------------------------------
// Discounts, slice A.
// ---------------------------------------------------------------------

// A discount that was deactivated cannot be chosen for a NEW application.
// It stays perfectly readable on every past application that references
// it, which is the whole point of deactivating rather than deleting (the
// owner's own live Shift4 account has two dated promotions sitting
// switched off exactly this way). Almost always a stale client: the
// picker was loaded before an Admin retired that discount. 409, so the UI
// reloads the list and asks again rather than treating it as a 404.
export class DiscountInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "discount_inactive");
    this.name = "DiscountInactiveError";
  }
}

// The same, for the discount REASON vocabulary. A separate code from
// discount_inactive because the UI's recovery differs: one reloads the
// discount picker, the other reloads the reason picker.
export class DiscountReasonInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "discount_reason_inactive");
    this.name = "DiscountReasonInactiveError";
  }
}

// The discount's own requires_reason flag is set and the request carried
// no discountReasonId. 400, not 409: the request itself is incomplete,
// and the fix is to send one, not to reload anything. The reason always
// comes from the configured list; there is no free-text path.
export class DiscountReasonRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 400, "discount_reason_required");
    this.name = "DiscountReasonRequiredError";
  }
}

// A ticket-level discount was applied to a line, or an item-level one to
// a ticket, against what the discount's own "Apply to" checkboxes allow.
// 409: the discount exists and the caller may apply discounts, but not
// this one here.
export class DiscountNotApplicableError extends DomainError {
  constructor(message: string) {
    super(message, 409, "discount_not_applicable");
    this.name = "DiscountNotApplicableError";
  }
}

// DSC36 (DEC-343). A bartender's discount that would take $0.00 off the
// guest's total: either existing discounts already give a bigger or equal
// reduction on every drink it could touch (per-drink non-stacking, DEC-042),
// or no drink on the ticket qualifies for it at all. Refused, nothing
// written. 409, the same "well-formed request, refused by current ticket
// state" shape as DiscountNotApplicableError. The message is a complete
// sentence for the floor to show as written.
export class DiscountSavesNothingError extends DomainError {
  constructor(message: string) {
    super(message, 409, "discount_saves_nothing");
    this.name = "DiscountSavesNothingError";
  }
}

// Taking a discount back off a ticket, where it is already off. 409, and
// deliberately distinct from a 404 on the application id: the row exists,
// it has simply already been removed, and a client that retried is not
// wrong, it is just late.
export class DiscountApplicationRemovedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "discount_application_removed");
    this.name = "DiscountApplicationRemovedError";
  }
}

// Correction pass, adversarial gate finding 2 (2026-08-29; docs/backlog.md
// AU2/AU2b). Raised by src/domain/payments.ts's payOrder, and by
// src/domain/managerExceptions.ts's assertNoOpenManagerExceptionBlocksOrder
// which it calls, BEFORE any adapter call is made, whenever this order
// already carries an unresolved manager_exceptions row.
//
// DEC-029 (2026-08-29): this error's ORIGINAL trigger (kind
// 'capture_shortfall_uncredited', a provider partial-approval leftover)
// was narrowed as unreachable against every adapter this codebase
// implements; see src/domain/managerExceptions.ts's own header for the
// full disposition. It is retained on a separate, reachable basis: AU8
// (docs/backlog.md), an overnight business-day-batch capture leaving an
// order genuinely short because its hold was smaller than the tab's final
// balance (kind 'business_day_capture_short'). Blocks every further
// payOrder call against a flagged order until a manager reviews it, the
// same "block-and-flag, never a silent second attempt" shape
// src/domain/errors.ts's own PaymentPendingReconciliationError already
// uses for a stuck Pending attempt. No automated resolution path exists
// in this slice, matching PaymentPendingReconciliationError's own
// documented gap; a manager resolves the row via
// src/domain/managerExceptions.ts's resolveManagerException (and the
// matching API route) once they have actually accounted for the money.
export class ManagerExceptionBlocksOrderError extends DomainError {
  constructor(message: string) {
    super(message, 409, "manager_exception_blocks_order");
    this.name = "ManagerExceptionBlocksOrderError";
  }
}

// PAY34-F1, PAY34-F2 (docs/build-briefs/PAY34-F1-pending-provider-captured-resolution.md section 2.2). A manager's
// resolution of a payment the provider and our record disagree about was refused, with nothing written: the provider
// does not show what the action needs, the row moved since, or the ticket no longer matches the payment. The message
// is a plain sentence naming the reason; it carries no provider payload and no card value.
export class ProviderResolutionRefusedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "provider_resolution_refused");
    this.name = "ProviderResolutionRefusedError";
  }
}

// B1 (docs/backlog.md, owner ruling 2026-08-27; migration 0050). A
// liquor product create/update collided with another row already holding
// the identical barcode for this tenant (db/schema.ts's
// liquor_products_tenant_barcode_key partial unique index, WHERE the
// column IS NOT NULL). Unlike DuplicateActiveNameError, which only blocks
// a collision among currently ACTIVE rows, this applies across every row
// for the tenant regardless of active state: see migration 0050's own
// header for why a deactivated bottle keeps its code reserved. The
// message names which field collided in plain language, per B1's
// acceptance criteria, so the caller does not have to guess from a bare
// 409. DEC-047 (migration 0088) retired B1's sibling supplier_sku field
// and its own partial unique index, which this error used to also
// translate.
export class DuplicateLiquorProductCodeError extends DomainError {
  constructor(message: string) {
    super(message, 409, "duplicate_liquor_product_code");
    this.name = "DuplicateLiquorProductCodeError";
  }
}

// CL6 (DEC-010, P0.15). Finalizing a business day is refused until that
// day is certainly OVER in the tenant's own configured time zone and
// cutoff (src/domain/businessDayCaptureSchedule.ts's
// isBusinessDateDueForCapture, the same boundary the overnight capture
// batch already trusts). Finalizing "today" mid-shift would snapshot a
// day still in motion.
export class BusinessDayNotOverError extends DomainError {
  constructor(message: string) {
    super(message, 409, "business_day_not_over");
    this.name = "BusinessDayNotOverError";
  }
}

// CL6 (DEC-010, P0.15). The day already has its business_day_closeouts
// row. The real enforcement is the table's own
// business_day_closeouts_tenant_date_key UNIQUE constraint; this error is
// the courteous translation of that refusal (or of the pre-check that
// spares the round trip), never the enforcement itself.
export class BusinessDayAlreadyFinalizedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "business_day_already_finalized");
    this.name = "BusinessDayAlreadyFinalizedError";
  }
}

// PAY1-S4-F2-F8-F5 slice 4 (PROVISIONAL). A day whose Stripe settlement differs (or has a dispute, a failed payout or an
// unresolved account, or an open settlement exception) is finalized only by a manager declaring the difference.
export class SettlementDifferenceNotDeclaredError extends DomainError {
  constructor(message: string) {
    super(message, 409, "settlement_difference_not_declared");
    this.name = "SettlementDifferenceNotDeclaredError";
  }
}

// The declared amounts are not the server's computed difference. The amounts are confirmed, never typed.
export class SettlementDifferenceMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "settlement_difference_mismatch");
    this.name = "SettlementDifferenceMismatchError";
  }
}

// PL1 (DEC-011 items 6 and 7; docs/build-briefs/PL1-payment-link.md).
// "One ACTIVE link per order at a time": createPaymentLink pre-checks
// this before writing anything, and migration 0056's own partial unique
// index (tenant_id, order_id WHERE status = 'Created') refuses the
// second row even if the pre-check regressed.
export class PaymentLinkAlreadyActiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_link_already_active");
    this.name = "PaymentLinkAlreadyActiveError";
  }
}

// PL1. cancelPaymentLink and reconcilePaymentLink both reject naming a
// link that does not exist for this tenant.
export class PaymentLinkNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "payment_link_not_found");
    this.name = "PaymentLinkNotFoundError";
  }
}

// PL1. A canceled (or otherwise already-terminal, non-Completed) link
// refuses completion outright: cancelPaymentLink rejects a link that
// already Completed (never moves backward), and reconcilePaymentLink's
// own "not Created any more" short-circuit means a link this codebase
// has already canceled never reaches payOrder even if the provider
// later reports the guest finished paying.
export class PaymentLinkNotCancelableError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_link_not_cancelable");
    this.name = "PaymentLinkNotCancelableError";
  }
}

// PAY16 (docs/backlog.md, VER34-provider-recheck-tests.md Section 5
// Finding C): a payment_links row's own provider_name (set once, at
// creation, from whichever adapter minted it) no longer matches the
// adapter now configured for this tenant/location. Same shape as
// SavedCardProviderMismatchError and TerminalProviderMismatchError, for
// the same reason: reachable only after a provider cutover left a stale
// row behind. Raised by reconcilePaymentLink and cancelPaymentLink
// before either calls the adapter. 409.
export class PaymentLinkProviderMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_link_provider_mismatch");
    this.name = "PaymentLinkProviderMismatchError";
  }
}

// PAY15 (docs/backlog.md, VER34-provider-recheck-tests.md Section 5
// Finding B): a payment_attempts row's own provider_name (set once, at
// charge time, from whichever adapter charged it) no longer matches the
// adapter now configured for this tenant/location. Same shape as
// SavedCardProviderMismatchError, TerminalProviderMismatchError and
// PaymentLinkProviderMismatchError above. Raised by refundPaymentLines
// before it calls the adapter. Not raised at all when the parent
// attempt's provider_name is NULL (a row written before this column
// existed): db/schema.ts's own comment on payment_attempts.providerName
// explains why that is "unknown," never a fabricated match or mismatch.
// 409.
export class RefundProviderMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "refund_provider_mismatch");
    this.name = "RefundProviderMismatchError";
  }
}

// INV4 (docs/backlog.md, docs/build-briefs/INV4-vendor-management.md).
// createVendorItemOffer refuses assigning a NEW offer to a vendor that
// has since been retired: retire-never-delete means the vendor's row and
// its past offers stay readable forever, but a retired vendor is not a
// valid target for a fresh purchasing relationship, the same "state-
// gated to an active row" discipline TerminalAlreadyInactiveError already
// establishes for terminals.
export class VendorInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "vendor_inactive");
    this.name = "VendorInactiveError";
  }
}

// INV4. "One preferred vendor per applicable location or purchasing
// scope" (P0.12's third vendor criterion): createVendorItemOffer/
// updateVendorItemOffer pre-check this before writing anything, and
// migration 0076's own partial unique index (tenant_id,
// liquor_product_id WHERE preferred = true) refuses the second row even
// if the pre-check regressed, the identical PaymentLinkAlreadyActiveError
// shape above.
export class VendorOfferAlreadyPreferredError extends DomainError {
  constructor(message: string) {
    super(message, 409, "vendor_offer_already_preferred");
    this.name = "VendorOfferAlreadyPreferredError";
  }
}

// INVLOC1 Slice 5. A location cannot choose an offer that has been retired: it would resolve to nothing and the location would
// silently fall back to the franchisee default behind the person's back.
export class VendorOfferInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "vendor_offer_inactive");
    this.name = "VendorOfferInactiveError";
  }
}

// TF4-F1 (docs/backlog.md; docs/build-briefs/TF4-F1-abc2go-vendor-
// marker.md). buildAbc2goExport (src/domain/abc2goExport.ts) raises this
// when no vendor on the tenant carries vendors.is_abc2go_export_target =
// true: the export must refuse with a plain message rather than silently
// producing a file with every line omitted, the same "the data this
// write/read depends on is not in the right state yet" 409 shape
// VendorInactiveError and VendorOfferAlreadyPreferredError already use.
export class NoAbc2goVendorMarkedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "no_abc2go_vendor_marked");
    this.name = "NoAbc2goVendorMarkedError";
  }
}

// TF4-F3. The ABC2GO export carries whole bottles, and a line with an ABC2GO
// code whose bottle count is unknown (no pack size) or not whole is refused
// rather than exported as a guess. The same 409 "the data this depends on is
// not in the right state yet" shape as NoAbc2goVendorMarkedError.
export class Abc2goBottleCountUnknownError extends DomainError {
  constructor(message: string) {
    super(message, 409, "abc2go_bottle_count_unknown");
    this.name = "Abc2goBottleCountUnknownError";
  }
}

// TF4-F1. Raised when a create/update would mark a SECOND vendor as the
// ABC2GO export target for the same tenant, the application-level catch
// of migration 0164's own partial unique index
// (vendors_one_abc2go_export_target_key), the identical
// "pre-check regressed, the database still refuses it" defense-in-depth
// VendorOfferAlreadyPreferredError already documents for its own sibling
// constraint.
export class VendorAlreadyAbc2goExportTargetError extends DomainError {
  constructor(message: string) {
    super(message, 409, "vendor_already_abc2go_export_target");
    this.name = "VendorAlreadyAbc2goExportTargetError";
  }
}

// TM1 (docs/backlog.md; DEC-230). Raised by src/domain/tabMerge.ts's
// mergeTabs whenever a precondition refuses the merge outright: the same
// tab named twice, either tab missing or already closed/canceled, the two
// tabs on different tenants or business days, or either tab mid-payment
// (a pending payment attempt), partially paid (a captured payment already
// covers some of it) or carrying a split plan still in progress. 409, the
// same "not in a state this command may act on" status every other
// state-gate refusal in this file uses (OrderClosedError,
// OrderBalanceOutstandingError). The message always says which tab and
// which reason, since the bartender reading it has to explain it to a
// guest.
export class TabMergeRefusedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "tab_merge_refused");
    this.name = "TabMergeRefusedError";
  }
}

// CORE6 (docs/build-briefs/CORE6-move-one-drink-to-another-tab.md). A single
// drink cannot move to the chosen tab. The message always says which reason,
// since the bartender reading it has to explain it to a guest.
export class LineMoveRefusedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "line_move_refused");
    this.name = "LineMoveRefusedError";
  }
}

// P9 (docs/build-briefs/P9-pour-volume-append-only-write.md). Once a
// tenant's ITEM2-F7 cutover state is 'complete', item_kind_pour_volumes
// is append-only at the database: migration 0133's a_item2_f7_pour_guard
// trigger (item2_f7_guard_policy_evidence) refuses any UPDATE or DELETE
// on that table for a complete tenant outright, regardless of role,
// pg 23514 "complete tenant ... pour evidence is append-only". Before
// this error existed, src/domain/vocabularies.ts's setItemKindPourVolumes
// always did a full DELETE-then-INSERT, so any save that dropped or
// reordered an already-assigned pour size hit that trigger and surfaced
// as an opaque 500 (owner report 2026-09-29, stage0 62f91c59) rather than
// a message a manager could act on. 409, the same "well-formed request,
// refused by current server state" shape DiscountNotApplicableError and
// TabMergeRefusedError above already use: the request is not malformed
// (that stays a plain ValidationError/400), it is simply impossible to
// satisfy as sent while this tenant's pour evidence can only grow.
export class PourVolumeRemovalRefusedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "pour_volume_removal_refused");
    this.name = "PourVolumeRemovalRefusedError";
  }
}

// P9-F2 (docs/build-briefs/P9-F2-pour-size-save-version-check.md; DEC-367).
// A pour-size save carried a version older than the kind's current list:
// another manager changed it after this editor loaded it. 409, refused
// before any write, so the screen reloads the latest list.
export class PourVolumesChangedError extends DomainError {
  constructor() {
    super("Changed by someone else", 409, "pour_volumes_changed");
    this.name = "PourVolumesChangedError";
  }
}

// ---------------------------------------------------------------------
// M5 (docs/build-briefs/M5-till-price-change.md; DEC-282): the till price
// change.
// ---------------------------------------------------------------------

// A price change needs a reason from the tenant's own list, and the request
// carried none. 400: the request is incomplete, the fix is to send one.
export class PriceChangeReasonRequiredError extends DomainError {
  constructor(message: string) {
    super(message, 400, "price_change_reason_required");
    this.name = "PriceChangeReasonRequiredError";
  }
}

// The reason was retired since the picker loaded. 409, so the UI reloads
// the reason list and asks again, the same shape as DiscountReasonInactiveError.
export class PriceChangeReasonInactiveError extends DomainError {
  constructor(message: string) {
    super(message, 409, "price_change_reason_inactive");
    this.name = "PriceChangeReasonInactiveError";
  }
}

// A payment link is out for the ticket (status Created) and covers the line.
// The guest may already be paying the amount it was built with, so the line's
// price cannot move until the link is canceled. 409: nothing is wrong with the
// request, the ticket is in a state that blocks it.
export class PaymentLinkOutError extends DomainError {
  constructor(message: string) {
    super(message, 409, "payment_link_out");
    this.name = "PaymentLinkOutError";
  }
}

// M5-R4: a price change key was reused for a request that differs from the
// original (another price or reason, or a set versus a put-back). 409: the
// retry is refused rather than answered with a result it did not ask for.
export class PriceChangeReplayMismatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "price_change_replay_mismatch");
    this.name = "PriceChangeReplayMismatchError";
  }
}

// M5-R4-F1 (DEC-334): an idempotency key was reused for a request whose content differs from the one
// that first used it. 409: the retry is refused rather than answered with a result it did not ask for.
// Same code as the older command-specific refusals (device setup code, vendor price list, invoices).
export class IdempotencyRequestMismatchError extends DomainError {
  constructor() {
    super("This request was already used for a different change.", 409, "idempotency_key_reused");
    this.name = "IdempotencyRequestMismatchError";
  }
}

// The line carries a changed price and the command would silently drop it
// (changing its liquor re-prices it from the menu). The caller must confirm
// first (confirmPriceRestore), because the price the bartender agreed to no
// longer has a basis. 409: nothing is wrong with the request, it needs a yes.
export class PriceChangeInForceError extends DomainError {
  constructor(message: string) {
    super(message, 409, "price_change_in_force");
    this.name = "PriceChangeInForceError";
  }
}

// PO10 (docs/build-briefs/PO10-vendor-price-list-import.md; DEC-294). The
// price list import's named refusals. Each carries a stable `code` the screen
// maps to its own message (design/mockups/vendor-price-list-import states 1 to
// 8), never by reading the human text.

/** A file the import cannot use: wrong type, unreadable, too big, too many
 * rows, or no code or price column. `found` lists the column headings that WERE
 * in the file, for the two column refusals, so the screen can offer them. */
export class PriceListFileError extends DomainError {
  readonly found: string[] | undefined;

  constructor(message: string, statusCode: number, code: string, found?: string[]) {
    super(message, statusCode, code);
    this.name = "PriceListFileError";
    this.found = found;
  }
}

// The vendor in the URL is not the one marked as the ABC2GO order file vendor.
// 409: the vendor exists, it is not in the state this command needs.
export class VendorNotPriceListVendorError extends DomainError {
  constructor(message: string) {
    super(message, 409, "vendor_not_price_list_vendor");
    this.name = "VendorNotPriceListVendorError";
  }
}

// A preview token that is unknown, another tenant's, another vendor's or another
// user's. One error for all four so a caller learns nothing about which.
export class PriceListPreviewNotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 404, "price_list_preview_not_found");
    this.name = "PriceListPreviewNotFoundError";
  }
}

// The check is older than 30 minutes, so prices may have moved. 410.
export class PriceListPreviewExpiredError extends DomainError {
  constructor(message: string) {
    super(message, 410, "price_list_preview_expired");
    this.name = "PriceListPreviewExpiredError";
  }
}

// The preview was already applied. 409: a preview is single-use.
export class PriceListPreviewUsedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "price_list_preview_used");
    this.name = "PriceListPreviewUsedError";
  }
}

// Fewer than 20% of the vendor's coded offers were in the file: probably not the
// right file, so Update is refused. 409.
export class PriceListNotAMatchError extends DomainError {
  constructor(message: string) {
    super(message, 409, "price_list_not_a_match");
    this.name = "PriceListNotAMatchError";
  }
}

// An idempotency key already used by a different import command. 409.
export class PriceListIdempotencyKeyReusedError extends DomainError {
  constructor(message: string) {
    super(message, 409, "idempotency_key_reused");
    this.name = "PriceListIdempotencyKeyReusedError";
  }
}

// R5 (DEC-313 ruling 1). Switching off a role that people still hold is refused,
// and the refusal NAMES them so the screen can offer to move them. 409.
export interface RoleHolderSummary {
  userId: string;
  displayName: string;
  /** The other roles the person holds, so the screen can say whether moving
   * them leaves them with no access. */
  otherRoles: { role: string; label: string }[];
}

export class RoleHeldError extends DomainError {
  readonly holders: RoleHolderSummary[];

  constructor(message: string, holders: RoleHolderSummary[]) {
    super(message, 409, "role_held");
    this.name = "RoleHeldError";
    this.holders = holders;
  }
}

// R5 (DEC-313 ruling 4). A copy that would hand out tasks the copier cannot give
// out is refused whole, naming those tasks in plain words. 403.
export class RoleCopyRefusedError extends DomainError {
  readonly taskLabels: string[];

  constructor(message: string, taskLabels: string[]) {
    super(message, 403, "role_copy_over_grant");
    this.name = "RoleCopyRefusedError";
    this.taskLabels = taskLabels;
  }
}

// R5 (DEC-313 ruling 3). A role name is already used by another role of the same
// tenant, switched off or not. 409. Carries which role, so a name taken by a
// switched-off role can offer to switch that one back on.
export class RoleNameTakenError extends DomainError {
  readonly takenByRole: string;
  readonly takenByActive: boolean;

  constructor(message: string, takenByRole: string, takenByActive: boolean) {
    super(message, 409, "role_name_taken");
    this.name = "RoleNameTakenError";
    this.takenByRole = takenByRole;
    this.takenByActive = takenByActive;
  }
}

// R6 (DEC-313 ruling 2). A staged role save carries the affected count the person
// confirmed; if the people who hold the role changed since, the save is refused so
// the confirmation is never stale. 409.
export class RoleConfirmationStaleError extends DomainError {
  readonly affectedCount: number;

  constructor(message: string, affectedCount: number) {
    super(message, 409, "confirmation_stale");
    this.name = "RoleConfirmationStaleError";
    this.affectedCount = affectedCount;
  }
}

// RCPT1-S2 (docs/build-briefs/RCPT1-S2-printers-and-cloudprnt.md). A printer command refused on a state the caller could not have known
// to avoid: the name is taken at that location, the location has its maximum, the printer is paired and must be unpaired first, the printer is
// not available for new jobs, or its queue is full. All 409, plain wording, no secret in any of them.
export class PrinterRefusedError extends DomainError {
  constructor(message: string, code: string) {
    super(message, 409, code);
    this.name = "PrinterRefusedError";
  }
}

// RCPT1-S3-F2. A ticket whose card is authorized for its total, waiting for a tip written on the receipt, takes no change to what it
// totals until the tip is entered: a capture is refused when the ticket outgrew its authorization, and a smaller ticket would be
// charged for drinks that are gone. Plain wording for the floor.
export class TicketAwaitingTipError extends DomainError {
  constructor() {
    super("This ticket is waiting for a tip. Enter the tip before changing it.", 409, "ticket_awaiting_tip");
    this.name = "TicketAwaitingTipError";
  }
}

// INVIMP1. A file the inventory import cannot use: wrong type, unreadable, too big, too many rows, no such sheet.
export class InventoryImportFileError extends DomainError {
  constructor(message: string, statusCode: number, code: string) {
    super(message, statusCode, code);
    this.name = "InventoryImportFileError";
  }
}

// INVIMP1. A second apply for an upload id that is still running. 409: try again in a moment.
export class InventoryImportBusyError extends DomainError {
  constructor() {
    super("This import is already running.", 409, "inventory_import_busy");
    this.name = "InventoryImportBusyError";
  }
}
